import { Component, DestroyRef, effect, inject, input, signal, untracked } from "@angular/core";
import { WeatherService } from "../../../core/services/weather.service";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { WeatherForecast } from "src/app/core/data-models/weather-forecast.model";
import { DatePipe } from '@angular/common';


@Component({
  selector: 'app-weather-widget',
  templateUrl: './weather-widget.component.html',
  styleUrls: ['./weather-widget.component.scss'],
  imports: [DatePipe]
})
export class WeatherWidgetComponent {
    private weatherService = inject(WeatherService);
    private forecastSub?: Subscription;

    latitude = input<number | null>(null);
    longitude = input<number | null>(null);
    locationLabel = input<string | null>(null);
    quickLook = input(false);

    weatherData: WeatherForecast | null = null;

    selectedHour = signal(0);
    isLoading = signal(false);
    errorMessage = signal<string | null>(null);

    readonly hazardousConditions = ['Thunderstorm', 'Thunderstorm with hail', 'Snow', 'Snow showers', 'Rain', 'Rain showers'];
    readonly hazardousWind = 10;

    readonly conditionIcons: Record<string, string> = {
        'Clear': 'bi-sun',
        'Mainly clear': 'bi-sun',
        'Partly cloudy': 'bi-cloud-sun',
        'Overcast': 'bi-cloud',
        'Fog': 'bi-cloud-fog',
        'Drizzle': 'bi-cloud-drizzle',
        'Rain': 'bi-cloud-rain',
        'Rain showers': 'bi-cloud-rain-heavy',
        'Snow': 'bi-snow',
        'Snow showers': 'bi-cloud-snow',
        'Thunderstorm': 'bi-cloud-lightning',
        'Thunderstorm with hail': 'bi-cloud-hail'
    };

    constructor() {
        effect(() => {
            this.latitude();
            this.longitude();
            untracked(() => {
                this.weatherData = null;
                this.selectedHour.set(0);
                this.loadForecast();
            });
        });

        inject(DestroyRef).onDestroy(() => this.forecastSub?.unsubscribe());
    }

    loadForecast(): void {
        this.forecastSub?.unsubscribe();
        const lat = this.latitude();
        const lon = this.longitude();
        if (lat == null || lon == null) return;

        this.isLoading.set(true);
        this.errorMessage.set(null);

        this.forecastSub = this.weatherService.getForecast(lat, lon).pipe(
            finalize(() => this.isLoading.set(false))
        ).subscribe({
            next: data => this.weatherData = data,
            error: (err: Error) => this.errorMessage.set(err.message)
        });
    }

    isHazardousHour(period: any): boolean {
        return this.isHazardousConditions(period.conditions) || this.isHazardousWind(period.windSpeed);
    }

    isHazardousWind(windSpeed: number): boolean {
        return windSpeed >= this.hazardousWind;
    }

    isHazardousConditions(conditions: string): boolean {
        return this.hazardousConditions.includes(conditions);
    }

    hasCoordinates(): boolean {
        return this.latitude() != null && this.longitude() != null;
    }
}